import { useCallback, useState } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useSearchParams } from "react-router-dom";
import { FaSearch, FaUsers } from "react-icons/fa";
import BadgeIcon from "../components/BadgeIcon";
import EmptyState from "../components/EmptyState";
import Skeleton, { SkeletonAvatar, SkeletonText } from "../components/Skeleton";
import PageWithRail from "../components/PageWithRail";
import ProfileTabLoadMore from "../components/ProfileTabLoadMore";
import { usePaginatedTab } from "../hooks/usePaginatedTab";
import {
  getAvatarUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";

export default function Writers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [error, setError] = useState("");

  const fetchWritersPage = useCallback(
    async (page, limit) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (query.trim()) params.set("q", query.trim());

      const res = await fetch(
        getApiUrl(`/api/user/writers?${params.toString()}`),
        { credentials: "include" }
      );
      if (!res.ok) {
        setError("Failed to load writers. Please try again.");
        throw new Error(`HTTP ${res.status}`);
      }

      setError("");
      const data = await res.json();
      return { items: data.users || [], pagination: data.pagination };
    },
    [query]
  );

  const writersFeed = usePaginatedTab({
    enabled: true,
    resetKey: query,
    pageSize: 20,
    fetchPage: fetchWritersPage,
  });

  const writers = writersFeed.items;

  const handleSearch = (e) => {
    e.preventDefault();
    const next = searchInput.trim();
    setQuery(next);
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <PageWithRail title="Writers">
      <div className="page-head">
        <h1 className="page-head__title">Writers</h1>
        <p className="page-head__subtitle">Discover poets on PeerPoetry</p>

        <form onSubmit={handleSearch} className="page-search">
          <FaSearch className="page-search__icon" aria-hidden="true" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or pen name..."
            className="page-search__input"
            aria-label="Search writers"
          />
        </form>
      </div>

      <div className="home-page__feed-list w-full space-y-3">
        {writersFeed.loading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="page-list-card pointer-events-none">
              <SkeletonAvatar size={56} />
              <div className="flex-1 min-w-0">
                <Skeleton className="h-5 w-32 mb-2 bg-[#eeeeee]" />
                <SkeletonText lines={2} />
              </div>
            </div>
          ))
        ) : error && writers.length === 0 ? (
          <div className="text-center peer-home-card p-6">
            <p className="text-red-700 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => writersFeed.reload()}
              className="peer-home-btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : writers.length === 0 ? (
          <EmptyState
            icon="user"
            title={query ? `No writers found for "${query}"` : "No writers yet"}
            message={
              query
                ? "Try a different name or pen name."
                : "When poets join PeerPoetry, they'll appear here."
            }
          />
        ) : (
          <>
            {writers.map((writer) => (
              <Link
                key={writer._id}
                to={`/users/${writer.username}`}
                className="page-list-card"
              >
                <img
                  src={getAvatarUrl(writer.avatar)}
                  alt=""
                  className="page-list-card__avatar"
                  onError={(e) => {
                    e.currentTarget.src =
                      e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                        ? FALLBACK_AVATAR_SVG
                        : DEFAULT_AVATAR_DATA_URL;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="page-list-card__title">
                      {writer.username}
                    </span>
                    <BadgeIcon type={writer.badge} className="text-[#f26522]" />
                  </div>
                  {writer.name && (
                    <p className="page-list-card__meta">{writer.name}</p>
                  )}
                  {writer.bio && (
                    <p className="page-list-card__body line-clamp-2">
                      {writer.bio}
                    </p>
                  )}
                  <p className="page-list-card__stat">
                    <FaUsers className="w-3 h-3" aria-hidden="true" />
                    {writer.followerCount || 0} follower
                    {(writer.followerCount || 0) === 1 ? "" : "s"}
                  </p>
                </div>
              </Link>
            ))}

            <ProfileTabLoadMore
              sentinelRef={writersFeed.sentinelRef}
              loadingMore={writersFeed.loadingMore}
              hasNextPage={writersFeed.pagination.hasNextPage}
              onLoadMore={writersFeed.loadMore}
              itemCount={writers.length}
            />
          </>
        )}
      </div>
    </PageWithRail>
  );
}
